import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Globe, Search, CheckCircle, AlertTriangle, Trash2, Loader2, Download, Shield, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_LABELS = { business: 'Firmowy', personal: 'Osobisty', disposable: 'Jednorazowy', business_general: 'Ogolny', business_personal: 'Imienny', free_email: 'Darmowy', risky: 'Ryzykowny', unknown: 'Nieznany' };
const TYPE_COLORS = {
  business: 'text-[hsl(var(--success))]', personal: 'text-[hsl(var(--info))]', disposable: 'text-[hsl(var(--destructive))]',
  business_general: 'text-[hsl(var(--success))]', business_personal: 'text-primary', free_email: 'text-[hsl(var(--info))]',
  risky: 'text-[hsl(var(--destructive))]', unknown: 'text-muted-foreground'
};

export default function ToolsPage() {
  const [tab, setTab] = useState('harvester');

  // Harvester
  const [harvUrls, setHarvUrls] = useState('');
  const [harvKeyword, setHarvKeyword] = useState('');
  const [harvMaxPages, setHarvMaxPages] = useState(5);
  const [harvResults, setHarvResults] = useState(null);
  const [harvLoading, setHarvLoading] = useState(false);
  const [harvSelected, setHarvSelected] = useState(new Set());
  const [harvPhoneSelected, setHarvPhoneSelected] = useState(new Set());

  // Verifier
  const [verEmails, setVerEmails] = useState('');
  const [verResults, setVerResults] = useState(null);
  const [verLoading, setVerLoading] = useState(false);

  // Dedup
  const [dedupResult, setDedupResult] = useState(null);
  const [dedupLoading, setDedupLoading] = useState(false);

  // Classifier
  const [classResult, setClassResult] = useState(null);
  const [classLoading, setClassLoading] = useState(false);

  const runHarvest = async () => {
    setHarvLoading(true);
    setHarvResults(null);
    try {
      const urls = harvUrls.split('\n').map(u => u.trim()).filter(Boolean);
      const r = await api.post('/tools/harvest-emails', { urls, keyword: harvKeyword, max_pages: harvMaxPages });
      setHarvResults(r.data);
      setHarvSelected(new Set(r.data.emails?.filter(e => e.type !== 'disposable').map(e => e.email) || []));
      setHarvPhoneSelected(new Set(r.data.phones?.map(p => p.phone) || []));
      toast.success(`Znaleziono ${r.data.found_emails} emaili i ${r.data.found_phones} telefonow`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setHarvLoading(false); }
  };

  const importHarvested = async () => {
    const emails = harvResults?.emails?.filter(e => harvSelected.has(e.email)) || [];
    const phones = harvResults?.phones?.filter(p => harvPhoneSelected.has(p.phone)) || [];
    if (!emails.length && !phones.length) { toast.error('Zaznacz emaile lub telefony do importu'); return; }
    try {
      const r = await api.post('/tools/harvest-to-contacts', { emails, phones });
      toast.success(`Zaimportowano ${r.data.imported} kontaktow (duplikaty: ${r.data.duplicates})`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
  };

  const toggleHarvEmail = (email) => {
    setHarvSelected(prev => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; });
  };
  const toggleHarvPhone = (phone) => {
    setHarvPhoneSelected(prev => { const n = new Set(prev); n.has(phone) ? n.delete(phone) : n.add(phone); return n; });
  };

  const runVerify = async () => {
    setVerLoading(true);
    setVerResults(null);
    try {
      const emails = verEmails.split('\n').map(e => e.trim()).filter(Boolean);
      const r = await api.post('/tools/verify-emails', { emails });
      setVerResults(r.data);
      toast.success(`Sprawdzono ${r.data.total} adresow`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setVerLoading(false); }
  };

  const runDedup = async () => {
    setDedupLoading(true);
    try {
      const r = await api.post('/tools/deduplicate');
      setDedupResult(r.data);
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setDedupLoading(false); }
  };

  const runClassify = async () => {
    setClassLoading(true);
    try {
      const r = await api.post('/tools/classify-contacts', { limit: 100 });
      setClassResult(r.data);
      toast.success(`Sklasyfikowano ${r.data.total_classified} kontaktow`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setClassLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="tools-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Narzedzia</h1>
        <p className="text-sm text-muted-foreground mt-1">Zbieracz emaili, weryfikacja, deduplikacja i klasyfikacja kontaktow</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="harvester"><Globe className="h-3.5 w-3.5 mr-1.5" />Zbieracz emaili</TabsTrigger>
          <TabsTrigger value="verify"><Shield className="h-3.5 w-3.5 mr-1.5" />Weryfikacja</TabsTrigger>
          <TabsTrigger value="dedup"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Deduplikacja</TabsTrigger>
          <TabsTrigger value="classify"><Sparkles className="h-3.5 w-3.5 mr-1.5" />Klasyfikator</TabsTrigger>
        </TabsList>

        {/* Email Harvester */}
        <TabsContent value="harvester" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Zbieracz adresow email z WWW</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">Podaj adresy stron lub slowo kluczowe. System przeszuka strony i wyciagnie adresy email.</p>
                <div className="space-y-2">
                  <Label className="text-xs">Adresy URL (jeden na linie)</Label>
                  <Textarea value={harvUrls} onChange={e => setHarvUrls(e.target.value)} rows={4} placeholder={"https://example.com/kontakt\nhttps://firmaXYZ.pl/o-nas"} data-testid="harvest-urls" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Lub szukaj po slowie kluczowym</Label>
                  <Input value={harvKeyword} onChange={e => setHarvKeyword(e.target.value)} placeholder="np. kawiarnie Warszawa" data-testid="harvest-keyword" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Max stron do przeszukania</Label>
                  <Input type="number" min={1} max={20} value={harvMaxPages} onChange={e => setHarvMaxPages(parseInt(e.target.value) || 5)} className="w-24" />
                </div>
                <Button onClick={runHarvest} disabled={harvLoading || (!harvUrls && !harvKeyword)} className="w-full" data-testid="harvest-submit">
                  {harvLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                  Szukaj adresow email
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base">Wyniki ({(harvResults?.found_emails || 0) + (harvResults?.found_phones || 0)})</CardTitle>
                  {(harvResults?.found_emails > 0 || harvResults?.found_phones > 0) && (
                    <Button size="sm" onClick={importHarvested} data-testid="harvest-import">
                      <Download className="h-3.5 w-3.5 mr-1.5" />Importuj ({harvSelected.size + harvPhoneSelected.size})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!harvResults ? (
                  <div className="py-12 text-center text-muted-foreground text-sm"><Globe className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />Uruchom zbieracz aby zobaczyc wyniki</div>
                ) : (harvResults.found_emails === 0 && harvResults.found_phones === 0) ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">Nie znaleziono adresow ani telefonow</div>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {harvResults.emails?.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Emaile ({harvResults.found_emails})</p>
                        <div className="space-y-1">
                          {harvResults.emails.map((e, i) => (
                            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/30 text-sm">
                              <Checkbox checked={harvSelected.has(e.email)} onCheckedChange={() => toggleHarvEmail(e.email)} />
                              <span className="font-mono text-xs flex-1">{e.email}</span>
                              <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[e.type] || ''}`}>{TYPE_LABELS[e.type] || e.type}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {harvResults.phones?.length > 0 && (
                      <div>
                        <Separator />
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2 mt-3">Telefony ({harvResults.found_phones})</p>
                        <div className="space-y-1">
                          {harvResults.phones.map((p, i) => (
                            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/30 text-sm">
                              <Checkbox checked={harvPhoneSelected.has(p.phone)} onCheckedChange={() => toggleHarvPhone(p.phone)} />
                              <span className="font-mono text-xs flex-1">{p.phone}</span>
                              <Badge variant="outline" className={`text-[10px] ${p.type === 'mobile' ? 'text-[hsl(var(--success))]' : p.type === 'landline' ? 'text-[hsl(var(--info))]' : 'text-muted-foreground'}`}>
                                {p.type === 'mobile' ? 'Komorkowy' : p.type === 'landline' ? 'Stacjonarny' : 'Nieznany'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {harvResults?.errors?.length > 0 && (
                  <div className="mt-3"><p className="text-xs text-destructive">Bledy: {harvResults.errors.join('; ')}</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Verification */}
        <TabsContent value="verify" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Weryfikacja adresow email</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">Sprawdz poprawnosc adresow email: skladnia, rekordy MX, domeny jednorazowe. Max 100 naraz.</p>
                <div className="space-y-2">
                  <Label className="text-xs">Adresy email (jeden na linie)</Label>
                  <Textarea value={verEmails} onChange={e => setVerEmails(e.target.value)} rows={8} placeholder={"jan@firma.pl\ninfo@nieistniejaca.xyz\ntest@mailinator.com"} data-testid="verify-emails" />
                </div>
                <Button onClick={runVerify} disabled={verLoading || !verEmails} className="w-full" data-testid="verify-submit">
                  {verLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                  Weryfikuj adresy
                </Button>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wyniki weryfikacji</CardTitle></CardHeader>
              <CardContent>
                {!verResults ? (
                  <div className="py-12 text-center text-muted-foreground text-sm"><Shield className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />Wklej adresy i kliknij Weryfikuj</div>
                ) : (
                  <div>
                    <div className="flex gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-[hsl(var(--success))]/5 border border-[hsl(var(--success))]/20 flex-1 text-center">
                        <p className="text-lg font-semibold font-heading text-[hsl(var(--success))]">{verResults.valid}</p>
                        <p className="text-xs text-muted-foreground">Poprawne</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[hsl(var(--destructive))]/5 border border-[hsl(var(--destructive))]/20 flex-1 text-center">
                        <p className="text-lg font-semibold font-heading text-[hsl(var(--destructive))]">{verResults.invalid}</p>
                        <p className="text-xs text-muted-foreground">Niepoprawne</p>
                      </div>
                    </div>
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                      {verResults.results.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/30 text-xs">
                          {r.valid ? <CheckCircle className="h-3.5 w-3.5 text-[hsl(var(--success))] shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--destructive))] shrink-0" />}
                          <span className="font-mono flex-1">{r.email}</span>
                          {r.issues?.length > 0 && <span className="text-destructive">{r.issues.join(', ')}</span>}
                          {r.mx && <span className="text-muted-foreground">MX: {r.mx}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Deduplication */}
        <TabsContent value="dedup" className="mt-4">
          <Card className="border-border/50 bg-card/80 max-w-lg">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Deduplikacja kontaktow</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Znajdz i usun duplikaty kontaktow na podstawie numeru telefonu i adresu email. Zachowywany jest pierwszy dodany kontakt.</p>
              <Button onClick={runDedup} disabled={dedupLoading} className="w-full" data-testid="dedup-submit">
                {dedupLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Znajdz i usun duplikaty
              </Button>
              {dedupResult && (
                <div className="p-4 rounded-lg bg-muted/30 border border-border/30 space-y-2">
                  <p className="text-sm">Duplikaty telefonow: <span className="font-mono font-semibold">{dedupResult.phone_duplicates}</span></p>
                  <p className="text-sm">Duplikaty emaili: <span className="font-mono font-semibold">{dedupResult.email_duplicates}</span></p>
                  <Separator />
                  <p className="text-sm font-medium text-[hsl(var(--success))]">Usunieto: {dedupResult.removed} duplikatow</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Classifier */}
        <TabsContent value="classify" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Klasyfikator kontaktow</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">Automatycznie podziel kontakty na kategorie wg typu adresu email: ogolne firmowe, imienne, darmowe, ryzykowne.</p>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Typy klasyfikacji:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded bg-muted/30 border border-border/30"><Badge variant="outline" className="text-xs text-[hsl(var(--success))]">Ogolny firmowy</Badge><p className="text-[10px] text-muted-foreground mt-1">info@, biuro@, kontakt@</p></div>
                    <div className="p-2 rounded bg-muted/30 border border-border/30"><Badge variant="outline" className="text-xs text-primary">Imienny firmowy</Badge><p className="text-[10px] text-muted-foreground mt-1">jan.kowalski@firma.pl</p></div>
                    <div className="p-2 rounded bg-muted/30 border border-border/30"><Badge variant="outline" className="text-xs text-[hsl(var(--info))]">Darmowy</Badge><p className="text-[10px] text-muted-foreground mt-1">Gmail, WP, Onet itp.</p></div>
                    <div className="p-2 rounded bg-muted/30 border border-border/30"><Badge variant="outline" className="text-xs text-[hsl(var(--destructive))]">Ryzykowny</Badge><p className="text-[10px] text-muted-foreground mt-1">noreply@, bounce@, abuse@</p></div>
                  </div>
                </div>
                <Button onClick={runClassify} disabled={classLoading} className="w-full" data-testid="classify-submit">
                  {classLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                  Klasyfikuj kontakty (max 100)
                </Button>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wyniki klasyfikacji</CardTitle></CardHeader>
              <CardContent>
                {!classResult ? (
                  <div className="py-12 text-center text-muted-foreground text-sm"><Sparkles className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />Kliknij Klasyfikuj aby rozpoczac</div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(classResult.summary).map(([key, count]) => (
                        <div key={key} className="p-3 rounded-lg bg-muted/30 border border-border/30 text-center">
                          <p className={`text-lg font-semibold font-heading ${TYPE_COLORS[key] || ''}`}>{count}</p>
                          <p className="text-xs text-muted-foreground">{TYPE_LABELS[key] || key}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Lacznie sklasyfikowano: {classResult.total_classified} kontaktow. Typ zapisany w polu email_type kazdego kontaktu.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
